import { Observable, Subject } from 'rxjs';
import { share, map } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
  EventEmitter,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';

import { SnTeam } from '@lib/api/entities';
import { SnTeamsAPI } from '@lib/api/services';

class TeamSelectComponentBase {
  constructor(
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
  ) {}
}

@Component({
  selector: 'sn-team-select',
  templateUrl: './team-select.component.html',
  styleUrls: ['./team-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['disabled'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SnTeamSelectComponent,
    },
  ],
})
export class SnTeamSelectComponent
  extends TeamSelectComponentBase
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string>
{
  public static nextId = 0;

  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    if (value !== this.value) {
      this._value = value;
      this.stateChanges.next();
      this._controlValueAccessorChangeFn(value);
    }
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(required: boolean) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  @Output()
  public readonly valueChange = new EventEmitter<SnTeam>();

  @HostBinding()
  public id = `sn-team-select-${SnTeamSelectComponent.nextId++}`;

  @HostBinding('attr.aria-describedby')
  public describedBy = '';

  @ViewChild(MatSelect)
  public select: MatSelect;

  public readonly options$: Observable<SnTeam[]>;

  public focused = false;
  public hidden = true;

  public stateChanges: Subject<void> = new Subject();

  public disabled: boolean;

  public errorState: boolean;

  public controlType = 'sn-team-select';

  private _value: string;
  private _placeholder: string;
  private _required = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public readonly ngControl: NgControl,
    private readonly _teamsService: SnTeamsAPI,
  ) {
    super(parentForm, parentFormGroup, ngControl, defaultErrorStateMatcher);

    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    this.options$ = this._teamsService.getTeamList({ pageSize: 'all' }).pipe(
      map((response) => response.results),
      share(),
    );
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return !this.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.stateChanges.next();
    }
  }

  public ngOnInit(): void {
    this.stateChanges.next();
  }

  public ngOnDestroy(): void {
    this.valueChange.complete();
    this.stateChanges.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackTeam(index: number, item: SnTeam): any {
    return item.id;
  }

  public onSelected(item: SnTeam): void {
    this.value = item ? item.id : null;
    this.valueChange.emit(item);
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent): void {
    this.select.open();
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  public registerOnTouched(): void {}

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};
}
